import { z } from 'zod'

/**
 * Shared field rules (VAL-005/006/013/014/016/017, CAL-023). Request schemas in
 * `contracts/` compose these so the web forms and the API agree (P-UI-008).
 */

/** C0 controls other than TAB, LF and CR, plus DEL (VAL-006). */
export function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if ((code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) || code === 0x7f) {
      return true
    }
  }
  return false
}

/** A real calendar date in YYYY-MM-DD that round-trips; year 0000 rejected. */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

/** A UTC instant `YYYY-MM-DDTHH:mm:ss(.sss)?Z` that round-trips (CAL-023). */
export function isIsoInstant(value: string): boolean {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d{1,3})?Z$/.exec(value)
  if (!match?.[1] || !isIsoDate(match[1])) return false
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return false
  return parsed.toISOString().slice(11, 19) === `${match[2]}:${match[3]}:${match[4]}`
}

/** Normalise a valid instant to millisecond precision with `Z`. */
export function normalizeInstant(value: string): string {
  return new Date(value).toISOString()
}

/** ISIN format and Luhn check digit, letters expanded A=10..Z=35 (VAL-016/017). */
export function isValidIsin(value: string): boolean {
  if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(value)) return false
  const digits = value
    .split('')
    .map((ch) => (/[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch))
    .join('')
  let sum = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i])
    if (double) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
    double = !double
  }
  return sum % 10 === 0
}

/** Absolute http(s) URL with a hostname (VAL-014). */
export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.length > 0
  } catch {
    return false
  }
}

/** Trimmed text with length bounds and no control characters. */
export function text(min: number, max: number) {
  return z
    .string()
    .max(max)
    .refine((v) => !hasControlChars(v), { message: 'Control characters are not permitted.' })
    .transform((v) => v.trim())
    .refine((v) => v.length >= min, { message: `Enter at least ${min} characters.` })
}

/** A substantive review/admin reason (WF-006). */
export const reasonSchema = text(8, 12000)

export const isoDateSchema = z
  .string()
  .refine(isIsoDate, { message: 'Use an ISO date (YYYY-MM-DD).' })

export const isoInstantSchema = z
  .string()
  .refine(isIsoInstant, { message: 'Use a UTC timestamp (YYYY-MM-DDTHH:mm:ssZ).' })
  .transform(normalizeInstant)

export const httpUrlSchema = z
  .string()
  .max(2048)
  .refine(isHttpUrl, { message: 'Source URL must use http or https.' })

export const isinSchema = z.string().refine(isValidIsin, { message: 'ISIN is not valid.' })

export const idSchema = z.string().min(1).max(64)

export const revisionSchema = z.number().int().min(1)
